import httpClient from "@plugins/http";
import { ApiResponse } from "@apis/api.ts";
import { notNull } from "@utils/var.check.ts";
import {
  AlsContentMapNodesRecommendationModel,
  ContentMap,
  ContentMapApiModel,
  ContentMapInfo,
  ContentMapSubjectLevelModel,
  ContentMapNode,
  ContentMapTopicModel,
  LearningLevelModel,
  SearchQuestionActivityInContentMap,
  Subject,
} from "@modules/content-map/content-map-types.ts";

// Cache the promise so the API is called only once per page load
let subjectLevelContentMapPromise: Promise<ApiResponse<ContentMapSubjectLevelModel[]>> | null =
  null;

const fetchSubjectLevelAndStreamContentMap = (
  filterByMoeLibrary = false,
): Promise<ApiResponse<ContentMapSubjectLevelModel[]>> => {
  if (!subjectLevelContentMapPromise) {
    subjectLevelContentMapPromise = httpClient.get(`/apis/contentmap/subject-level`, {
      params: {
        filterByMoeLibrary: filterByMoeLibrary,
      },
    });
  }
  return subjectLevelContentMapPromise;
};

const fetchLevelAndStreamContentMap = (subjectIds) => {
  return httpClient.get(`/apis/contentmap/level-stream-content-map`, {
    params: {
      subjectIds: subjectIds,
    },
  });
};

const fetchLevelAndStreamContentMapByContentMapIds = (subjectIds, contentMapIds, includeAlsCM) => {
  return httpClient.get(`/apis/contentmap/level-stream-content-map/get-by-ids`, {
    params: {
      subjectIds: subjectIds,
      contentMapIds: contentMapIds,
      includeAlsCM: includeAlsCM,
    },
  });
};

const fetchContentMapAndTopics = (uuid) => {
  return httpClient.get(`/apis/contentmap/${uuid}`);
};

const fetchContentMapGetTopics = (uuid: string): Promise<ApiResponse<ContentMapTopicModel[]>> => {
  return httpClient.get(`/apis/contentmap/topics/${uuid}`);
};

const fetchContentMapList = (
  subjectId: number,
  levelAndStreamId: number,
  filterByMoeLibrary = false,
  filterByLearningProgress = false,
): Promise<ApiResponse<ContentMapApiModel[]>> => {
  return httpClient.get(`/apis/contentmap`, {
    params: {
      subjectId: subjectId,
      levelAndStreamId: levelAndStreamId,
      filterByMoeLibrary: filterByMoeLibrary,
      filterByLearningProgress: filterByLearningProgress,
    },
  });
};

const fetchManageContentMapList = (requestData) => {
  const data = new URLSearchParams();
  Object.keys(requestData).forEach((key) => {
    if (notNull(requestData[key])) {
      data.append(key, requestData[key]);
    }
  });
  return httpClient.get(`/apis/contentmap/manage-resource/list`, {
    params: data,
  });
};

const fetchMoeSubjects = async (): Promise<ApiResponse<Subject[]>> => {
  return await httpClient.get(`/apis/contentmap/moe-subjects`);
};

const fetchMyOwnerGroups = () => {
  return httpClient.get(`/apis/contentmap/owner-groups`);
};

const createContentMap = (requestData): Promise<ApiResponse<string>> => {
  return httpClient.post(`/apis/contentmap/create`, requestData);
};

// ApiResponse<string> -> return uuid
const updateContentMap = async (requestData): Promise<ApiResponse<string>> => {
  return await httpClient.post(`/apis/contentmap/edit`, requestData, {
    headers: {
      "X-With-Richtext": true,
    },
  });
};

const fetchContentMapNodesAndLearningOutcomes = async (
  uuid,
): Promise<ApiResponse<ContentMapNode[]>> => {
  return await httpClient.get(`/apis/contentmap/nodes/${uuid}`);
};

const fetchLearningOutcomeLearningLevel = (): Promise<ApiResponse<LearningLevelModel[]>> => {
  return httpClient.get(`/apis/learning-level`);
};

const fetchContentMapInfo = async (uuid): Promise<ApiResponse<ContentMapInfo>> => {
  return await httpClient.get(`/apis/contentmap/info/${uuid}`);
};

const deleteContentMap = (uuid) => {
  return httpClient.post(`/apis/contentmap/delete/${uuid}`);
};

const duplicateContentMap = (requestData) => {
  return httpClient.post(`/apis/contentmap/duplicate`, requestData);
};
const getContentMap = async (uuid): Promise<ApiResponse<ContentMap>> => {
  return await httpClient.get(`/apis/contentmap/get/${uuid}`);
};

const publishContentMapToAls = (uuid) => {
  return httpClient.post(`/apis/contentmap/als/publish/${uuid}`);
};

// Used in Als Landing Page
const fetchContentMapNodeSummary = (nodeUuid) => {
  return httpClient.get(`/apis/contentmap/node/summary/${nodeUuid}`);
};

// Used in Als Landing Page
const fetchContentMapSummary = (uuid) => {
  return httpClient.get(`/apis/contentmap/summary/${uuid}`);
};

// Used in Als Landing Page
const fetchAlsContentMapList = (requestData) => {
  return httpClient.get(`/apis/contentmap/als/list`, {
    params: requestData,
  });
};

const fetchAlsContentMap = (requestData) => {
  return httpClient.get(`/apis/contentmap/als`, {
    params: requestData,
  });
};

const searchQuestionActivity = async (
  requestData,
): Promise<ApiResponse<SearchQuestionActivityInContentMap[]>> => {
  return await httpClient.get(`/apis/contentmap/question-activity/search`, {
    params: requestData,
  });
};

const recommendNodesForAls = (
  requestData: AlsContentMapNodesRecommendationModel,
): Promise<ApiResponse> => {
  return httpClient.post(`/apis/contentmap/als/recommend-nodes`, requestData);
};

export default {
  fetchSubjectLevelAndStreamContentMap,
  fetchLevelAndStreamContentMap,
  fetchLevelAndStreamContentMapByContentMapIds,
  fetchContentMapNodesAndLearningOutcomes,
  fetchLearningOutcomeLearningLevel,
  fetchContentMapAndTopics,
  fetchContentMapGetTopics,
  fetchContentMapList,
  fetchManageContentMapList,
  fetchContentMapInfo,
  fetchMoeSubjects,
  fetchMyOwnerGroups,
  createContentMap,
  updateContentMap,
  deleteContentMap,
  duplicateContentMap,
  getContentMap,
  publishContentMapToAls,
  fetchContentMapNodeSummary,
  fetchContentMapSummary,
  fetchAlsContentMap,
  fetchAlsContentMapList,
  searchQuestionActivity,
  recommendNodesForAls,
};
